/**
 * Runtime config. The SPA is built once and told at boot which API and which
 * Entra tenant it talks to, so the static site needs no rebuild when the API
 * moves. Shipped as public/config.json, edited in place by whoever deploys.
 */

export type RuntimeConfig = {
  apiEndpoint: string;
  /** Entra directory. The MSAL authority is derived from it, nothing else. */
  tenantId: string;
  /** The showling-family app registration's appId GUID. */
  clientId: string;
  /**
   * What MSAL asks for, e.g. api://<familyAppId>/access.
   *
   * Its own key rather than derived from clientId even though today they name
   * the same registration (entra_bootstrap.sh puts the SPA redirect URIs on
   * the registration that exposes the API). The moment the SPA gets its own
   * client — which it should, so the iOS app and the browser are separable —
   * the two diverge, and a derived value would break silently: MSAL would
   * still hand back a token, and the API would 401 on the `aud` claim.
   */
  apiScope: string;
  /** Public media host, e.g. https://<account>.blob.core.windows.net/media. */
  mediaDomain: string;
};

export class ConfigError extends Error {}

/**
 * mediaDomain is REQUIRED, unlike on AWS where web-stack.ts genuinely never
 * emitted it. main.bicep always composes MEDIA_DOMAIN, so the only way it can
 * go missing now is a hand-edited config.json — and its absence is silent in
 * the worst way: every gallery tile paints a placeholder and Watch reports
 * "the playback response carried no playable segment URLs", blaming the API
 * for a missing client key.
 */
const REQUIRED = ['apiEndpoint', 'tenantId', 'clientId', 'apiScope', 'mediaDomain'] as const;

/**
 * What ships in public/config.json and config.local.json.example until a human
 * runs entra_bootstrap.sh — which is what they hold TODAY.
 *
 * A SUBSTRING, so it also catches apiScope's `api://REPLACE_WITH_…/access`.
 * Half-filling is the likely mistake: two blanks, one appId to paste, and
 * Entra's answer to a scope that names a non-existent application is several
 * minutes and one full-page redirect away from the file that caused it.
 * admin/src/config.ts has matched the same way since the port.
 */
const PLACEHOLDER = 'REPLACE_WITH';

export async function loadConfig(): Promise<RuntimeConfig> {
  const res = await fetch('/config.json', { cache: 'no-store' });

  // A CDN with an SPA fallback maps 404 -> /index.html with statusCode 200, so
  // a MISSING config.json arrives as HTML with a perfectly happy status. That
  // was CloudFront's 404-to-index behaviour and it is Cloudflare Pages' SPA
  // fallback too, which is what serves this site now. res.ok is a lie here;
  // the JSON parse is the only honest signal. Never branch on status.
  const raw = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new ConfigError(
      'config.json did not parse as JSON — the SPA was served in its place, ' +
        'which means the deployment never wrote it. First bytes: ' +
        JSON.stringify(raw.slice(0, 120)),
    );
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new ConfigError('config.json parsed but is not an object');
  }
  const obj = parsed as Record<string, unknown>;

  const missing = REQUIRED.filter((k) => typeof obj[k] !== 'string' || !obj[k]);
  if (missing.length) {
    throw new ConfigError(`config.json is missing: ${missing.join(', ')}`);
  }

  const clientId = String(obj.clientId);
  const apiScope = String(obj.apiScope);
  if (clientId.includes(PLACEHOLDER) || apiScope.includes(PLACEHOLDER)) {
    // CAUGHT HERE, AND THE ERROR CLASS IS THE POINT. A placeholder is a
    // perfectly good non-empty string, so the REQUIRED check above waved it
    // through and auth.ts threw a plain Error one line later — which is not a
    // ConfigError, so boot.ts set `configFault: false` and App.tsx offered the
    // "Sign out and start again" button under it. That button is a dead end
    // here: it discards a diagnosis naming entra_bootstrap.sh and the exact az
    // command, then lands the parent on a sign-in screen whose only button
    // answers "auth used before configureAuth() — boot order changed", because
    // MSAL was never constructed. A reload brought the diagnosis back and
    // nothing said so. Shipping as a ConfigError keeps the button off and the
    // sentence on screen.
    throw new ConfigError(
      'config.json still carries the placeholders it ships with. The ' +
        'showling-family app registration does not exist until a human runs ' +
        'azure/entra_bootstrap.sh — the service principal has no Microsoft ' +
        'Graph access, so nothing can create it unattended — and that script ' +
        'prints the appId to paste into clientId and into apiScope. Read it ' +
        'back with: az ad app list --display-name showling-family --query ' +
        '"[0].appId" -o tsv',
    );
  }

  return {
    apiEndpoint: String(obj.apiEndpoint).replace(/\/+$/, ''),
    tenantId: String(obj.tenantId),
    clientId,
    apiScope,
    mediaDomain: String(obj.mediaDomain).replace(/\/+$/, ''),
  };
}
